/**
 * @typedef {Object} maploc
 * @property {Number} x
 * @property {Number} y
 *
 * @typedef {Object} mapPoint
 * @property {Number} lat
 * @property {Number} lng
 * 
 * @typedef {Object} mapRefPoint
 * @property {Number} lat
 * @property {Number} lng
 * @property {Number} x
 * @property {Number} y
 */

import ZMath from "./ZMath";

class ZMap {

  /**
   * 
   * @param {mapPoint} p1 
   * @param {mapPoint} p2 
   * @returns 
   */
  static haversineDistance(p1, p2) {
      const R = 6371; // Radius of the Earth in kilometers

      const dLat = ZMath.Deg2Rad(p2.lat - p1.lat);
      const dLon = ZMath.Deg2Rad(p2.lng - p1.lng);

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(ZMath.Deg2Rad(p1.lat)) * Math.cos(ZMath.Deg2Rad(p2.lat)) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      const distance = R * c;
      return distance;
  }

  /**
   * 
   * @param {mapPoint} center 
   * @param {mapPoint} point 
   * @param {Number} radiusKm 
   * @returns 
   */
  static isPointInCircle(center, point, radiusKm) {
      const distance = this.haversineDistance(center, point);
      return distance <= radiusKm;
  }

  /**
   * 
   * @param {mapPoint} loc 
   * @param {mapRefPoint[]} refs 
   * @returns {maploc}
   */
  static GPS2XY(loc, refs){
    
    if (refs.length < 3) {
      throw new Error("At least three reference points are required.");
    }

    const ref1 = refs[0];
    const ref2 = refs[1];
    const ref3 = refs[2];

    const dLat1 = loc.lat - ref1.lat;
    const dLng1 = loc.lng - ref1.lng;

    const dLat2 = ref2.lat - ref1.lat;
    const dLng2 = ref2.lng - ref1.lng;

    const dLat3 = ref3.lat - ref1.lat;
    const dLng3 = ref3.lng - ref1.lng;

    const det = dLat2 * dLng3 - dLat3 * dLng2;

    if (det === 0) {
      throw new Error("Reference points are collinear.");
    }

    const x = ((dLat1 * dLng3 - dLat3 * dLng1) / det) * (ref2.x - ref1.x) +
          ((dLat2 * dLng1 - dLat1 * dLng2) / det) * (ref3.x - ref1.x) + ref1.x;

    const y = ((dLat1 * dLng3 - dLat3 * dLng1) / det) * (ref2.y - ref1.y) +
          ((dLat2 * dLng1 - dLat1 * dLng2) / det) * (ref3.y - ref1.y) + ref1.y;

    return { x, y };

  }

  /**
   * 
   * @param {maploc} loc 
   * @param {mapRefPoint[]} refs 
   * @returns {mapPoint}
   */
  static XY2GPS(loc, refs){
    if (refs.length < 3) {
      throw new Error("At least three reference points are required.");
    }

    const ref1 = refs[0];
    const ref2 = refs[1];
    const ref3 = refs[2];

    const dx1 = loc.x - ref1.x;
    const dy1 = loc.y - ref1.y;

    const dx2 = ref2.x - ref1.x;
    const dy2 = ref2.y - ref1.y;

    const dx3 = ref3.x - ref1.x;
    const dy3 = ref3.y - ref1.y;

    const det = dx2 * dy3 - dx3 * dy2;

    if (det === 0) {
      throw new Error("Reference points are collinear.");
    }

    const dLat1 = ref2.lat - ref1.lat;
    const dLng1 = ref2.lng - ref1.lng;

    const dLat2 = ref3.lat - ref1.lat;
    const dLng2 = ref3.lng - ref1.lng;

    const lat = ((dx1 * dy3 - dx3 * dy1) / det) * dLat1 +
          ((dx2 * dy1 - dx1 * dy2) / det) * dLat2 + ref1.lat;

    const lng = ((dx1 * dy3 - dx3 * dy1) / det) * dLng1 +
          ((dx2 * dy1 - dx1 * dy2) / det) * dLng2 + ref1.lng;

    return { lat, lng };
  }

}

export default ZMap;



